import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Closes a popover/menu when the user clicks outside the returned ref.
 *
 * The callback can change on every render; we keep the latest version in a
 * ref so the DOM listener is registered exactly once per mount.
 */
export default function useOutsideClick(onOutside) {
  const ref = useRef(null);
  const cbRef = useRef(onOutside);

  // Always sync the latest callback without re-subscribing the listener.
  useEffect(() => {
    cbRef.current = onOutside;
  }, [onOutside]);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        cbRef.current?.(e);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
    // `ref` and `cbRef` are refs (guaranteed stable by React) – subscribing
    // once per mount is intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return ref;
}

/**
 * Dropdown open/close state + outside-click close. `setOpen` from useState is
 * guaranteed stable by React, so the useCallback wrappers have empty deps.
 */
export function useDropdown() {
  const [open, setOpen] = useState(false);
  // setOpen is stable per React docs – safe to omit from deps.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const close = useCallback(() => setOpen(false), []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const toggle = useCallback(() => setOpen((o) => !o), []);
  const ref = useOutsideClick(close);
  return { open, setOpen, toggle, close, ref };
}
